import { Dispatch, FC, SetStateAction } from "react";
import { FormType } from "../../common";
import { DsmGrid, DsmIcon } from "@dsm-dcs/design-system-react";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { useFormContext } from "react-hook-form";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import { Button } from "@material-ui/core";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { CattleEmission } from "../../../../models/Intervention/CattleIntervention";
import { Spacer } from "../../../helpers/Spacer";

interface BeefInterventionEmissionMitigationFormProps {
  formType: FormType;
  fieldItemPrefix: string;
  beefEmissionsData: CattleEmission;
  isEmissionEdit: boolean;
  setIsEmissionEdit: Dispatch<SetStateAction<boolean>>;
  resetEdit: () => void;
}

const BeefInterventionEmissionMitigationForm: FC<BeefInterventionEmissionMitigationFormProps> = ({
  formType,
  fieldItemPrefix,
  beefEmissionsData,
  isEmissionEdit,
  setIsEmissionEdit,
  resetEdit,
}) => {
  const intl = useIntl();
  const formContext = useFormContext();
  const classes = processAndStageStylesV2() as CSSClassesList;

  return (
    <>
      <Spacer />
      <DsmGrid className={classes.dsmGridOneColumnWithBorder}>
        <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
          <Spacer />
          <Spacer />
          {!isEmissionEdit ? (
            <>
              {!!formContext.getValues(`${fieldItemPrefix}.methaneEntericFermentation`) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.methaneEntericFermentation`}
                    label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.METHANE_ENTERIC_FERMENTATION' })}
                    tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.METHANE_ENTERIC_FERMENTATION.TOOLTIP' })}
                    type="number"
                    metricUnit="%"
                    baseline={beefEmissionsData?.methaneEntericFermentation}
                    disabled
                    displayTypeSelect={false} />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalMethaneEntericFermentation`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.METHANE_ENTERIC_FERMENTATION' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.METHANE_ENTERIC_FERMENTATION.TOOLTIP' })}
                  type="number"
                  adornment="%"
                  defaultValue={beefEmissionsData?.methaneEntericFermentation?.toString()}
                  disabled
                />
              )}
              {!!formContext.getValues(`${fieldItemPrefix}.nmvoc`) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.nmvoc`}
                    label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NON_METHANE_VOLATILE_ORGANIC_COMPOUNDS_NO_SOURCE' })}
                    tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NON_METHANE_VOLATILE_ORGANIC_COMPOUNDS_NO_SOURCE' })}
                    type="number"
                    metricUnit="%"
                    baseline={beefEmissionsData?.nmvoc}
                    disabled
                    displayTypeSelect={false}
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalNmvoc`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NON_METHANE_VOLATILE_ORGANIC_COMPOUNDS_NO_SOURCE' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NON_METHANE_VOLATILE_ORGANIC_COMPOUNDS_NO_SOURCE' })}
                  type="number"
                  adornment="%"
                  defaultValue={beefEmissionsData?.nmvoc?.toString()}
                  disabled
                />
              )}
              {!!formContext.getValues(`${fieldItemPrefix}.methaneMms`) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.methaneMms`}
                    label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.METHANE_FROM_MANURE' })}
                    tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.METHANE_FROM_MANURE' })}
                    type="number"
                    metricUnit="%"
                    baseline={beefEmissionsData?.methaneMms}
                    disabled
                    displayTypeSelect={false}
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalMethaneMms`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.METHANE_FROM_MANURE' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.METHANE_FROM_MANURE' })}
                  type="number"
                  adornment="%"
                  defaultValue={beefEmissionsData?.methaneMms?.toString()}
                  disabled
                />
              )}
              {!!formContext.getValues(`${fieldItemPrefix}.nitrousOxideLeaching`) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.nitrousOxideLeaching`}
                    label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_EMISSION_MANURE_LEACHING' })}
                    tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_EMISSION_MANURE_LEACHING' })}
                    type="number"
                    metricUnit="%"
                    baseline={beefEmissionsData?.nitrousOxideLeaching}
                    disabled
                    displayTypeSelect={false}
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalNitrousOxideLeaching`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_EMISSION_MANURE_LEACHING' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_EMISSION_MANURE_LEACHING' })}
                  type="number"
                  adornment="%"
                  defaultValue={beefEmissionsData?.nitrousOxideLeaching?.toString()}
                  disabled
                />
              )}
              {!!formContext.getValues(`${fieldItemPrefix}.nitrousOxideDirect`) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.nitrousOxideDirect`}
                    label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_DIRECT_NO_SOURCE' })}
                    tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_DIRECT_NO_SOURCE' })}
                    type="number"
                    metricUnit="%"
                    baseline={beefEmissionsData?.nitrousOxideDirect}
                    disabled
                    displayTypeSelect={false}
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalNitrousOxideDirect`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_DIRECT_NO_SOURCE' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_DIRECT_NO_SOURCE' })}
                  type="number"
                  adornment="%"
                  defaultValue={beefEmissionsData?.nitrousOxideDirect?.toString()}
                  disabled
                />
              )}
              {!!formContext.getValues(`${fieldItemPrefix}.nitrousOxideVolatilization`) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.nitrousOxideVolatilization`}
                    label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_VOLATILIZATION_FROM_MANURE' })}
                    tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_VOLATILIZATION_FROM_MANURE' })}
                    type="number"
                    metricUnit="%"
                    baseline={beefEmissionsData?.nitrousOxideVolatilization}
                    disabled
                    displayTypeSelect={false}
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalNitrousOxideVolatilization`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_VOLATILIZATION_FROM_MANURE' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_VOLATILIZATION_FROM_MANURE' })}
                  type="number"
                  adornment="%"
                  defaultValue={beefEmissionsData?.nitrousOxideVolatilization?.toString()}
                  disabled
                />
              )}
              {!!formContext.getValues(`${fieldItemPrefix}.nitrogenOxidesStorage`) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.nitrogenOxidesStorage`}
                    label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROGEN_OXIDE_FROM_MANURE_STORAGE' })}
                    tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROGEN_OXIDE_FROM_MANURE_STORAGE' })}
                    type="number"
                    metricUnit="%"
                    baseline={beefEmissionsData?.nitrogenOxidesStorage}
                    disabled
                    displayTypeSelect={false}
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalNitrogenOxidesStorage`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROGEN_OXIDE_FROM_MANURE_STORAGE' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROGEN_OXIDE_FROM_MANURE_STORAGE' })}
                  type="number"
                  adornment="%"
                  defaultValue={beefEmissionsData?.nitrogenOxidesStorage?.toString()}
                  disabled
                />
              )}
              {!!formContext.getValues(`${fieldItemPrefix}.ammoniaStorage`) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.ammoniaStorage`}
                    label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_STORAGE' })}
                    tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_STORAGE' })}
                    type="number"
                    metricUnit="%"
                    baseline={beefEmissionsData?.ammoniaStorage}
                    disabled
                    displayTypeSelect={false}
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalAmmoniaStorage`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_STORAGE' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_STORAGE' })}
                  type="number"
                  adornment="%"
                  defaultValue={beefEmissionsData?.ammoniaStorage?.toString()}
                  disabled
                />
              )}
              {!!formContext.getValues(`${fieldItemPrefix}.ammoniaHousing`) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.ammoniaHousing`}
                    label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_EXCRETED_IN_HOUSING' })}
                    tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_EXCRETED_IN_HOUSING' })}
                    type="number"
                    metricUnit="%"
                    baseline={beefEmissionsData?.ammoniaHousing}
                    disabled
                    displayTypeSelect={false}
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalAmmoniaHousing`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_EXCRETED_IN_HOUSING' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_EXCRETED_IN_HOUSING' })}
                  type="number"
                  adornment="%"
                  defaultValue={beefEmissionsData?.ammoniaHousing?.toString()}
                  disabled
                />
              )}
              {!!formContext.getValues(`${fieldItemPrefix}.ammoniaYard`) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.ammoniaYard`}
                    label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_EXCRETED_IN_YARD' })}
                    tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_EXCRETED_IN_YARD' })}
                    type="number"
                    metricUnit="%"
                    baseline={beefEmissionsData?.ammoniaYard}
                    disabled
                    displayTypeSelect={false}
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalAmmoniaYard`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_EXCRETED_IN_YARD' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_EXCRETED_IN_YARD' })}
                  type="number"
                  adornment="%"
                  defaultValue={beefEmissionsData?.ammoniaYard?.toString()}
                  disabled
                />
              )}
              {!!formContext.getValues(`${fieldItemPrefix}.pm2_5`) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.pm2_5`}
                    label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.PARTICULATE_MATTER_SMALLER_THAN_2_5' })}
                    tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.PARTICULATE_MATTER_SMALLER_THAN_2_5' })}
                    type="number"
                    metricUnit="%"
                    baseline={beefEmissionsData?.pm2_5}
                    disabled
                    displayTypeSelect={false}
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalPm2_5`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.PARTICULATE_MATTER_SMALLER_THAN_2_5' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.PARTICULATE_MATTER_SMALLER_THAN_2_5' })}
                  type="number"
                  adornment="%"
                  defaultValue={beefEmissionsData?.pm2_5?.toString()}
                  disabled
                />
              )}
              {!!formContext.getValues(`${fieldItemPrefix}.pm10`) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.pm10`}
                    label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.PARTICULATE_MATTER_SMALLER_THAN_10' })}
                    tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.PARTICULATE_MATTER_SMALLER_THAN_10' })}
                    type="number"
                    metricUnit="%"
                    baseline={beefEmissionsData?.pm10}
                    disabled
                    displayTypeSelect={false}
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalPm10`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.PARTICULATE_MATTER_SMALLER_THAN_10' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.PARTICULATE_MATTER_SMALLER_THAN_10' })}
                  type="number"
                  adornment="%"
                  defaultValue={beefEmissionsData?.pm10?.toString()}
                  disabled
                />
              )}
              {!!formContext.getValues(`${fieldItemPrefix}.tsp`) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.tsp`}
                    label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.TSP_NO_SOURCE' })}
                    tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.TSP_NO_SOURCE' })}
                    type="number"
                    metricUnit="%"
                    baseline={beefEmissionsData?.tsp}
                    disabled
                    displayTypeSelect={false}
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalTsp`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.TSP_NO_SOURCE' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.TSP_NO_SOURCE' })}
                  type="number"
                  adornment="%"
                  defaultValue={beefEmissionsData?.tsp?.toString()}
                  disabled
                />
              )}
              <div />
            </>
          ) : (
            <>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.methaneEntericFermentation`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.METHANE_ENTERIC_FERMENTATION' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.METHANE_ENTERIC_FERMENTATION.TOOLTIP' })}
                  type="number"
                  metricUnit="%"
                  baseline={beefEmissionsData?.methaneEntericFermentation}
                  disabled={formType === FormType.View}
                  displayTypeSelect
                />
                <Spacer />
              </div>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.nmvoc`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NON_METHANE_VOLATILE_ORGANIC_COMPOUNDS_NO_SOURCE' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NON_METHANE_VOLATILE_ORGANIC_COMPOUNDS_NO_SOURCE' })}
                  type="number"
                  metricUnit="%"
                  baseline={beefEmissionsData?.nmvoc}
                  disabled={formType === FormType.View}
                  displayTypeSelect
                />
                <Spacer />
              </div>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.methaneMms`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.METHANE_FROM_MANURE' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.METHANE_FROM_MANURE' })}
                  type="number"
                  metricUnit="%"
                  baseline={beefEmissionsData?.methaneMms}
                  disabled={formType === FormType.View}
                  displayTypeSelect
                />
                <Spacer />
              </div>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.nitrousOxideLeaching`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_EMISSION_MANURE_LEACHING' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_EMISSION_MANURE_LEACHING' })}
                  type="number"
                  metricUnit="%"
                  baseline={beefEmissionsData?.nitrousOxideLeaching}
                  disabled={formType === FormType.View}
                  displayTypeSelect
                />
                <Spacer />
              </div>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.nitrousOxideDirect`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_DIRECT_NO_SOURCE' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_DIRECT_NO_SOURCE' })}
                  type="number"
                  metricUnit="%"
                  baseline={beefEmissionsData?.nitrousOxideDirect}
                  disabled={formType === FormType.View}
                  displayTypeSelect
                />
                <Spacer />
              </div>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.nitrousOxideVolatilization`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_VOLATILIZATION_FROM_MANURE' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROUS_OXIDE_VOLATILIZATION_FROM_MANURE' })}
                  type="number"
                  metricUnit="%"
                  baseline={beefEmissionsData?.nitrousOxideVolatilization}
                  disabled={formType === FormType.View}
                  displayTypeSelect
                />
                <Spacer />
              </div>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.nitrogenOxidesStorage`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROGEN_OXIDE_FROM_MANURE_STORAGE' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.NITROGEN_OXIDE_FROM_MANURE_STORAGE' })}
                  type="number"
                  metricUnit="%"
                  baseline={beefEmissionsData?.nitrogenOxidesStorage}
                  disabled={formType === FormType.View}
                  displayTypeSelect
                />
                <Spacer />
              </div>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.ammoniaStorage`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_STORAGE' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_STORAGE' })}
                  type="number"
                  metricUnit="%"
                  baseline={beefEmissionsData?.ammoniaStorage}
                  disabled={formType === FormType.View}
                  displayTypeSelect
                />
                <Spacer />
              </div>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.ammoniaHousing`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_EXCRETED_IN_HOUSING' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_EXCRETED_IN_HOUSING' })}
                  type="number"
                  metricUnit="%"
                  baseline={beefEmissionsData?.ammoniaHousing}
                  disabled={formType === FormType.View}
                  displayTypeSelect
                />
                <Spacer />
              </div>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.ammoniaYard`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_EXCRETED_IN_YARD' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.AMMONIA_FROM_MANURE_EXCRETED_IN_YARD' })}
                  type="number"
                  metricUnit="%"
                  baseline={beefEmissionsData?.ammoniaYard}
                  disabled={formType === FormType.View}
                  displayTypeSelect
                />
                <Spacer />
              </div>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.pm2_5`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.PARTICULATE_MATTER_SMALLER_THAN_2_5' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.PARTICULATE_MATTER_SMALLER_THAN_2_5' })}
                  type="number"
                  metricUnit="%"
                  baseline={beefEmissionsData?.pm2_5}
                  disabled={formType === FormType.View}
                  displayTypeSelect
                />
                <Spacer />
              </div>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.pm10`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.PARTICULATE_MATTER_SMALLER_THAN_10' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.PARTICULATE_MATTER_SMALLER_THAN_10' })}
                  type="number"
                  metricUnit="%"
                  baseline={beefEmissionsData?.pm10}
                  disabled={formType === FormType.View}
                  displayTypeSelect
                />
                <Spacer />
              </div>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.tsp`}
                  label={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.TSP_NO_SOURCE' })}
                  tooltip={intl.formatMessage({ id: 'BASELINE.FORM.EMISSIONS.TSP_NO_SOURCE' })}
                  type="number"
                  metricUnit="%"
                  baseline={beefEmissionsData?.tsp}
                  disabled={formType === FormType.View}
                  displayTypeSelect
                />
                <Spacer />
              </div>
              <div />
            </>
          )}


          <div id='button-div'
            style={{
              color: 'var(--dsm-color-neutral-darker',
              left: '0',
              display: 'flex',
              flexDirection: 'column'
            }}>

            <Button
              href=""
              style={{
                textDecoration: 'underline',
                color: '#0070BA',
                display: (!isEmissionEdit && formType !== FormType.View) ? 'flex' : 'none',
                placeSelf: 'start',
                textTransform: 'none',
              }}
              onClick={() => setIsEmissionEdit(true)}
              disabled={(formType === FormType.View || isEmissionEdit === true)}
            >
              <DsmIcon
                slot="before"
                name="editor/pencil-01"
                style={{ paddingRight: '5px', height: '24px', width: '24px' }}
              />
              {intl.formatMessage({ id: 'GENERAL.EDIT_INPUT' })}
            </Button>

            {isEmissionEdit === true && (
              <Button
                href=""
                style={{
                  textDecoration: 'underline',
                  color: '#FF0000',
                  placeSelf: 'start',
                  textTransform: 'none',
                }}
                onClick={() => resetEdit()}
                disabled={formType === 'view'}
              >
                <DsmIcon
                  slot="before"
                  name="general/minus-circle"
                  style={{ paddingRight: '5px', height: '24px', width: '24px' }}
                />
                {intl.formatMessage({ id: 'GENERAL.CANCEL' })}
              </Button>
            )}
          </div>
        </DsmGrid>
      </DsmGrid>
    </>
  )
}

export default BeefInterventionEmissionMitigationForm;
